/* eslint-disable guard-for-in */
import axios from 'axios';
import get from 'lodash/get';
import isObject from 'lodash/isObject';
import isUndefined from 'lodash/isUndefined';
import noop from 'lodash/noop';

import * as Actions from '../../actions';
import { CoachmarkDismissReasons } from '../../constants/External';
import { PandoraTypes } from '../../constants/SourceTypes';
import { getInDomain } from '../../domains/externalUtils';
import { getMusicIdFromPandoraId } from '../../lib/PandoraIdUtil';
import { getMyMusicPath } from '../../lib/RouteUtils';
import { getStore } from '../../store/StoreAccessor';
import Logger from '../../tools/Logger';
import { getPandoraTypeFromPandoraId } from '../SourceTypeUtils';

import { sendAppCommand } from './Secretary';

const log = Logger.child({
  component: 'lib/PandoraApi/PandoraApiProtected',
});

const VERSION = __VERSION__;
const state = {};

function isDefined(params, key) {
  return !isUndefined(params) && !isUndefined(params[key]);
}

function performCallback(callback, data) {
  if (typeof callback === 'function') {
    callback(data);
  }
}

function errorCallback(callback, errorMessage, data) {
  const dataParam = data || {};
  dataParam.error = errorMessage;
  log.error(errorMessage);
  performCallback(callback, dataParam);
}

export function getProperty(propertyName) {
  if (propertyName === 'version') {
    return VERSION;
  }
}

export function getAdDemogCallback() {
  return state.ad_demog_callback;
}

export function supports(methodName) {
  sendAppCommand('supports', methodName);
}
/*
  This method really just plays a station. Note: the return values aren't being
  used, they're just there to make the control flow simpler
 */
export function playTrack(params, callback) {
  if (params) {
    if (!isUndefined(params.stationId) && !isUndefined(params.trackToken)) {
      return sendAppCommand(
        'ext_lcswti',
        {
          token: params.stationId,
          startingTrackToken: params.trackToken,
        },
        callback,
      );
    } else if (!isUndefined(params.stationId)) {
      // only stationId specified, launch the station
      return sendAppCommand(
        'ext_lsfi',
        {
          token: params.stationId,
        },
        callback,
      );
    }
  }
  return sendAppCommand('ext_play', {}, callback);
}

export function pauseTrack(params, callback) {
  const callbackArg = isObject(params) && params.value ? { value: params.value } : null;
  sendAppCommand('ext_pause_for_ad', '');
  performCallback(callback, callbackArg);
}

export function playOrPauseTrack(params, callback) {
  const callbackArg = isObject(params) && params.value ? { value: params.value } : null;
  sendAppCommand('ext_play_or_pause');
  performCallback(callback, callbackArg);
}

export function createStation(params, callback) {
  if (!isDefined(params, 'token')) {
    errorCallback(callback, 'Parameter "token" is required.');
    return;
  }

  if (!isDefined(params, 'type')) {
    errorCallback(callback, 'Parameter "type" is required.');
    return;
  }

  if (params.type === 'musicId') {
    sendAppCommand('ext_lsfmi', { token: params.token }, callback);
  } else if (params.type === 'stationId') {
    if (typeof params.startingTrackToken === 'undefined') {
      sendAppCommand('ext_lsfi', { token: params.token }, callback);
    } else {
      // used to launch a specific custom track inside a special station
      sendAppCommand(
        'ext_lcswti',
        {
          token: params.token,
          startingTrackToken: params.startingTrackToken,
          isLaunchingSpecialStationTrack: true,
        },
        callback,
      );
    }
  } else {
    errorCallback(callback, 'Parameter "type" must be of value "musicID" or "stationID".');
  }
}

export function openLandingPage(params, callback) {
  if (!isDefined(params, 'pageURL')) {
    errorCallback(callback, 'Parameter "pageURL" is required.');
    return;
  }

  state.currentLandingPage = window.open(params.pageURL);
  performCallback(callback);
}

export function closeLandingPage(params, callback) {
  if (state.currentLandingPage && typeof state.currentLandingPage.close === 'function') {
    state.currentLandingPage.close();
  } else {
    sendAppCommand('ext_close_ad');
  }

  performCallback(callback);
}

export function openExternalPage(params, callback) {
  if (!isDefined(params, 'url')) {
    errorCallback(callback, 'Parameter "url" is required.');
    return;
  }
  window.open(params.url);
  performCallback(callback);
}

export function playMovie(params, callback) {
  if (!isDefined(params, 'wifiURL') && !isDefined(params, 'cellURL')) {
    errorCallback(callback, 'Either parameter "wifiURL" or "cellURL" is required.');
    return;
  }

  const movieOptions = {
    url: params.wifiURL || params.cellURL,
    skipDelaySeconds: params.skipDelaySeconds,
    showScrubber: isDefined(params, 'showScrubber') ? params.showScrubber : true,
    usingAPVBackfill: params.usingAPVBackfill,
    learnMoreUrl: params.learnMoreUrl || '',
  };

  // start value exchange
  if (isDefined(params, 'offerName')) {
    movieOptions.offerName = params.offerName;
    movieOptions.rewardProperties = params.rewardProperties;
    movieOptions.rewardThresholdSeconds = params.rewardThresholdSeconds;
  }

  // 3rd party callbacks
  const keys = [
    'startUrl',
    'firstQuartileUrl',
    'secondQuartileUrl',
    'thirdQuartileUrl',
    'completeUrl',
  ];
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (isDefined(params, key)) {
      movieOptions[key] = encodeURIComponent(params[key]);
    }
  }

  sendAppCommand('ext_movie', movieOptions, callback);
}

export function share(params, callback) {
  if (!isDefined(params, 'type')) {
    errorCallback(callback, 'Parameter "type" is required.');
    return;
  }

  if (params.type === 'station') {
    sendAppCommand('ext_share_station', params.token);
    performCallback(callback);
  } else if (params.type === 'track') {
    sendAppCommand('ext_share_track', params.token);
    performCallback(callback);
  } else {
    errorCallback(callback, 'Parameter "type" must be of value "station" or "track".');
  }
}

export function offerTrial(params, callback) {
  sendAppCommand('ext_sub');
  performCallback(callback, { success: 'true' });
}

export function offerUpgrade(params, callback) {
  sendAppCommand('ext_sub', params);
  performCallback(callback);
}

export function disableVideoAdsUntilNextStationChange(params, callback) {
  sendAppCommand('ext_dis_video_ads');
  performCallback(callback);
}

// @todo - updating this method is currently deferred
// - we'll probably want to stop storing the user state on the window object
export function getP1SubscriberState(params, callback) {
  let substate = 'unknown';
  sendAppCommand('ext_sub_state');

  if (typeof window.listener_state !== 'undefined') {
    substate = window.listener_state.toLowerCase();
    window.listener_state = null;
  }

  performCallback(callback, { substate });
}

export function routeToNowPlaying() {
  sendAppCommand('ext_route_station');
}

export function fetchURL(params, callback) {
  if (!isDefined(params, 'url')) {
    errorCallback(callback, 'Parameter "url" is required.');
    return;
  }

  const type = params.method ? params.method : 'get';
  const headers = params.params ? params.params : {};

  axios({
    url: params.url,
    method: type,
    contentType: false,
    headers,
  })
    .then(() => {
      performCallback(callback);
    })
    .catch(error => {
      errorCallback(callback, error);
    });
}

export function recordAdClick(params, callback) {
  if (!isDefined(params, 'url')) {
    errorCallback(callback, 'Parameter "url" is required.');
    return;
  }
  axios(params.url)
    .then(() => {
      performCallback(callback);
    })
    .catch(error => {
      errorCallback(callback, error);
    });
}

export function openGenrePicker(params, callback) {
  if (!isDefined(params, 'category')) {
    errorCallback(callback, 'Parameter "category" is required.');
    return;
  }

  sendAppCommand('ext_genre', params);
  performCallback(callback);
}

export function playSample(params, callback) {
  if (!isDefined(params, 'samples')) {
    errorCallback(callback, 'Parameter "samples" is required.');
    return;
  }

  let samplesArray = params.samples;
  if (typeof samplesArray === 'string') {
    samplesArray = JSON.parse(samplesArray);
  }

  if (typeof samplesArray !== 'object') {
    errorCallback(
      callback,
      'Value of parameter "samples" must be an array or JSON string of sample dictionaries.',
    );
    return;
  }

  for (let i = 0; i < samplesArray.length; i++) {
    if (!isDefined(samplesArray[i], 'url')) {
      errorCallback(callback, 'Parameter "url" of each sample is required.');
      return;
    }

    if (!isDefined(samplesArray[i], 'musicId')) {
      errorCallback(callback, 'Parameter "musicId" of each sample is required.');
      return;
    }

    if (!isDefined(samplesArray[i], 'gain')) {
      errorCallback(callback, 'Parameter "gain" of each sample is required.');
      return;
    }
  }

  sendAppCommand('ext_play_sample', samplesArray);
  performCallback(callback);
}

export function stopSample(params, callback) {
  sendAppCommand('ext_stop_sample');
  performCallback(callback);
}

export function launchAddSeed(params, callback) {
  if (!isDefined(params, 'stationId')) {
    errorCallback(callback, 'Parameter "stationId" is required.');
    return;
  }

  sendAppCommand('ext_add_vrty', params.stationId);
  performCallback(callback);
}

export function startValueExchange(params, callback) {
  const unsuccessful = { success: 'false' };
  const parsedParams = typeof params === 'string' ? JSON.parse(params) : params;

  if (!isDefined(parsedParams, 'offerName')) {
    errorCallback(callback, 'Parameter "offerName" is required.', unsuccessful);
    return;
  }

  if (!isDefined(parsedParams, 'rewardProperties')) {
    errorCallback(callback, 'Parameter "rewardProperties" is required.', unsuccessful);
    return;
  }

  if (typeof parsedParams.rewardProperties === 'string') {
    parsedParams.rewardProperties = JSON.parse(parsedParams.rewardProperties);
  }

  if (!isDefined(parsedParams.rewardProperties, 'correlationId')) {
    errorCallback(
      callback,
      'Parameter "correlationId" of "rewardProperties" is required.',
      unsuccessful,
    );
  }

  if (!isDefined(parsedParams.rewardProperties, 'creativeId')) {
    errorCallback(
      callback,
      'Parameter "creativeId" of "rewardProperties" is required.',
      unsuccessful,
    );
    return;
  }

  if (!isDefined(parsedParams.rewardProperties, 'lineId')) {
    errorCallback(callback, 'Parameter "lineId" of "rewardProperties" is required.', unsuccessful);
    return;
  }

  if (!isDefined(parsedParams.rewardProperties, 'brandName')) {
    errorCallback(
      callback,
      'Parameter "brandName" of "rewardProperties" is required.',
      unsuccessful,
    );
    return;
  }

  if (!isDefined(parsedParams.rewardProperties, 'offerButtonLabel')) {
    parsedParams.rewardProperties.offerButtonLabel = '';
  }

  if (!isDefined(parsedParams.rewardProperties, 'offerButtonUrl')) {
    parsedParams.rewardProperties.offerButtonUrl = '';
  }

  if (!isDefined(parsedParams.rewardProperties, 'leadInBannerUrl')) {
    parsedParams.rewardProperties.leadInBannerUrl = '';
  }

  if (!isDefined(parsedParams.rewardProperties, 'leadInAudioUrl')) {
    parsedParams.rewardProperties.leadInAudioUrl = '';
  }

  sendAppCommand('ext_vx', parsedParams, callback);
}

/**
 * This method is used by display ads and some video ad templates to set context
 * specific ad identifiers that should be used internally, whenever these are
 * present (instead of any others).
 *
 * @note This method is currently unavoidable but should be deprecated in
 * the future. No additional features should use this method.
 * @param {object} event Event data with a "context" property that is either a
 * string or a JSON serializable object with the ad identifiers.
 * @param {Function} [callback] Optional error callback method
 * @returns {undefined}
 */
export function sendEvent(event, callback) {
  // eg. Pandora.sendEvent({context: { adIdentifiers : { creativeId: '1', lineId: '2', , "advertiserId" : "0", "campaignId" : "0", "siteId" : "0", placementId" : "0"}}}) called by Display Ads
  if (isDefined(event, 'context')) {
    let context;
    if (typeof event.context === 'string' || event.context instanceof String) {
      try {
        context = JSON.parse('' + event.context);
      } catch (ex) {
        errorCallback(callback, `error parsing json from sendEvent: ${event.context}`, ex);
        return;
      }
    } else if (typeof event.context === 'object') {
      context = event.context;
    } else {
      errorCallback(callback, `invalid sendEvent parameter: ${event.context}`);
      return;
    }
    sendAppCommand('ext_event', context);
  }
}

export function dismiss(params, callback) {
  const pandoraId = params.targetId;
  const store = getStore();
  const appState = store.getState();
  const closeCoachmarkParams = {
    reason: params && params.reason,
  };
  if (params.reason === CoachmarkDismissReasons.UPGRADE) {
    store.dispatch(getInDomain('analytics.actions.logClickedStartTrialPAUpsellCoachmark', noop)());
  } else if (params.reason === CoachmarkDismissReasons.START_STATION && pandoraId) {
    const musicId = getMusicIdFromPandoraId(pandoraId);
    if (get(params, 'isPaCap')) {
      // PA Cap: only create a station if no source is playing and it's an allowed type
      const currentSourceId = getInDomain('playback.utils.getCurrentPlaylist')(appState);
      const allowedTypes = [PandoraTypes.ARTIST, PandoraTypes.SONG];
      const pandoraType = getPandoraTypeFromPandoraId(pandoraId);
      if (!currentSourceId) {
        const errorRecoveryPath = getMyMusicPath();
        if (allowedTypes.includes(pandoraType)) {
          sendAppCommand(
            'ext_lsfmi',
            {
              token: musicId,
              forceReroute: true,
              errorRecoveryPath,
            },
            callback,
          );
        } else {
          store.dispatch(Actions.Application.reroute(errorRecoveryPath));
        }
      }
    } else {
      sendAppCommand(
        'ext_lsfmi',
        {
          token: musicId,
          forceReroute: true,
        },
        callback,
      );
    }
  }
  sendAppCommand('ext_close_coachmark', closeCoachmarkParams, callback);
}

export function sendAction(action, callback) {
  sendAppCommand('ext_action', action);
  performCallback(callback);
}

//------------------------------
//          OLD API
// for backwards compatibility
//------------------------------
export function pauseMusic(blnPause) {
  if (blnPause) {
    sendAppCommand('ext_pause');
  } else {
    playTrack();
  }
}

export function launchFriendsPanel() {
  sendAppCommand('ext_friends', '');
}

export function launchGenrePanel(genre) {
  // launchGenrePanel is deprecated in API v4
  // genre panel will be replaced by browse
  openGenrePicker({ category: genre });
}

export function launchCreateStation() {
  sendAppCommand('ext_sh_sc', '');
}

export function launchShare(shareType) {
  sendAppCommand('ext_share', shareType);
}

export function launchStationFromId(id) {
  createStation({ token: id, type: 'stationId' });
}

export function launchStationFromMusicId(musicId) {
  createStation({ token: musicId, type: 'musicId' });
}

export function launchCustomStationWithTrackId(stationId, trackId) {
  createStation({
    token: stationId,
    startingTrackToken: trackId,
    type: 'stationId',
  });
}

export function launchRegistration() {
  sendAppCommand('ext_reg', '1');
}

export function launchSubscription() {
  sendAppCommand('ext_sub', '1');
}

export function launchAddVariety(params, callback) {
  if (!isDefined(params, 'stationId')) {
    errorCallback(callback, 'Parameter "stationId" is required.');
    return;
  }

  sendAppCommand('ext_add_vrty', params.stationId);
  performCallback(callback);
}

export function launchRenewalPanel() {
  sendAppCommand('ext_renew');
}

export function declineRenewal() {
  sendAppCommand('ext_no_renew');
}

// An ad calls this when it needs to display an ad that uses demog info, the tuner calls a call back hook with the info
export function requestAdDemogCallback(callback) {
  // @todo - `ad_demog_callback` was set on the window object in PandoraApi v3.  Its now on a private variable, will likely require an accessor method for the tuner to get it.
  state.ad_demog_callback = callback;
  sendAppCommand('ext_demog_ad', '');
}

export function launchShareCurrentStation() {
  sendAppCommand('ext_lss', '');
}

export function getTrackingCodeCookie() {
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const crumb = cookies[i].split('=');
    if (crumb[0].replace(/^\s\s*/, '') === 'tc') {
      return crumb[1];
    }
  }
  return null;
}

// An ad calls this for Sponsored Listening reward state to delay ad refresh
export function delayAdRefresh(params) {
  sendAppCommand('ext_delay_ad_refresh', params.delaySeconds);
}

export function closeCoachmark() {
  sendAppCommand('ext_close_coachmark');
}

export function closeAd() {
  sendAppCommand('ext_close_ad');
}
